<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedOwners"
      multi-select
      searchable
      :class="meta.filter.quickFilter ? 'tw-min-w-[150px]' : 'tw-min-w-[250px]'"
      :label="meta.filter.quickFilter ? '' : $t(filterName)"
      :list="owners"
      :placeholder="
        meta.filter.quickFilter ? $t('All Owners') : $t(`Select ${filterName} to filter by`)
      "
      :max-labels-visible="meta.filter.quickFilter ? 1 : 5"
      can-deselect
      labels-primary
    >
      <template v-if="!meta.filter.quickFilter" #select-all-text>
        <WdsText :text="$t('Select all')" />
      </template>
    </WdsSelect>
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack, WdsText } from '@worksome/wds-components'
import { computed, ComputedRef, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { user } from '@/composables/useUser'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'

type OwnerFilter = ListItem & { id: string }

enum OwnerQueryType {
  Jobs = 'jobOwners',
  Hires = 'hireOwners',
  Employments = 'hiringManagers',
  Clients = 'clientOwners',
}

const { meta } = defineProps<{ meta: FilterMetaDataProp }>()

const selectedOwners: ModelRef<OwnerFilter[]> = defineModel({
  default: () => [],
})

const { selectedAccounts } = useAccountsStore()

const queries = {
  [OwnerQueryType.Jobs]: graphql(`
    query JobOwners($accounts: [ID!]) {
      jobs(accounts: $accounts, first: 100000) {
        data {
          owners {
            id
            value: id
            label: name
          }
        }
      }
    }
  `),
  [OwnerQueryType.Hires]: graphql(`
    query Owners($accounts: [ID!]) {
      hires(accounts: $accounts, first: 100000) {
        data {
          owners {
            id
            user {
              id
              value: id
              label: name
            }
          }
        }
      }
    }
  `),
  [OwnerQueryType.Clients]: graphql(`
    query SupplierClientOwnersFilterOptions($accounts: [ID!]) {
      companyClients(accounts: $accounts, first: 100000) {
        data {
          id
          owners {
            id
            user {
              id
              value: id
              label: name
            }
          }
        }
      }
    }
  `),
  [OwnerQueryType.Employments]: graphql(`
    query HiringManagers($accounts: [ID!]) {
      employments(accounts: $accounts, first: 100000) {
        data {
          hiringManagers {
            id
            value: id
            label: name
          }
        }
      }
    }
  `),
}

const dataExtractors = {
  [OwnerQueryType.Jobs]: (result: any) => result?.jobs?.data,
  [OwnerQueryType.Hires]: (result: any) => result?.hires?.data,
  [OwnerQueryType.Employments]: (result: any) => result?.employments?.data,
  [OwnerQueryType.Clients]: (result: any) => result?.companyClients?.data,
}

const filterNames = {
  [OwnerQueryType.Jobs]: 'Job owner(s)',
  [OwnerQueryType.Hires]: 'Hire owner(s)',
  [OwnerQueryType.Employments]: 'Hiring manager(s)',
  [OwnerQueryType.Clients]: 'Client owner(s)',
}

const queryType = computed(() => {
  const context = meta.filter.name as OwnerQueryType

  if (Object.values(OwnerQueryType).includes(context)) {
    return context
  }

  return OwnerQueryType.Jobs
})

const selectedQuery = computed(() => queries[queryType.value])
const dataExtractor = computed(() => dataExtractors[queryType.value])
const filterName = computed(() => filterNames[queryType.value])

const { result } = useQuery(
  selectedQuery,
  () => ({
    ...(selectedAccounts.length === 0
      ? {}
      : { accounts: selectedAccounts.map((account) => account.id) }),
  }),
  {
    clientId: 'publicApi',
  },
)

const owners: ComputedRef<OwnerFilter[]> = computed(() => {
  const data = dataExtractor.value(result.value)

  if (!data) {
    return []
  }

  const allOwners: OwnerFilter[] = data.flatMap((item) => {
    if (queryType.value === OwnerQueryType.Employments) {
      return item?.hiringManagers ?? []
    }
    // Client and hire owners are assignments; the filter takes the user behind each.
    if (queryType.value === OwnerQueryType.Clients || queryType.value === OwnerQueryType.Hires) {
      return (item?.owners ?? []).map((owner) => owner.user)
    }
    return item?.owners ?? []
  })

  const uniqueOwners: OwnerFilter[] = Array.from(
    new Map(allOwners.map((owner) => [owner.id, owner])).values(),
  )

  return uniqueOwners
    .map((owner) =>
      owner.id === user.value.globalId
        ? { ...owner, label: $t('Me (:user)', { user: owner.label }) }
        : owner,
    )
    .sort((a, b) => (a.id === user.value.globalId ? -1 : b.id === user.value.globalId ? 1 : 0))
})
</script>
